// ── NAMED DATE MARKERS — ONE LIST, EVERY TAB ────────────────────────────────
// Ben marks moments in the business ("changed the opt-in copy on 24 Aug") and
// wants "everything since then" at one click, on whichever tab he is reading.
//
// STORAGE: ONE ROW in ry_settings, key MARKERS_KEY, value a JSON array of
//   { id, name, date }   — date is a bare YYYY-MM-DD.
// carried by the existing GET/POST /settings routes. No new table, column or
// endpoint. POST is owner-only, so the write paths here 403 for a viewer; the
// controls that call them are hidden for anyone but the owner.
//
// SHARING: this is a module-level store, read once per page load and shared
// by every component that calls useMarkers() — Pages & Masterclass, Ad
// Metrics, Traffic & UTM and Compare. A marker added on one tab is in the
// list the moment the save returns, with no reload, because the list is the
// same object. useSyncExternalStore keeps every mounted subscriber in step.
//
// FAILURE: a failed save leaves the list exactly as it was — the caller's
// promise rejects, and the caller keeps the typed values on screen. A failed
// load leaves an empty list with `error` set; the built-in timeframe options
// never depend on this module succeeding.
import { useEffect, useSyncExternalStore } from 'react';
import { getAdminSettings, saveAdminSetting } from '../../utils/api';

export const MARKERS_KEY   = 'timeframe_markers';
export const MARKER_PREFIX = 'marker:';

/** A range id of the form "marker:<id>", used by the shared timeframe control. */
export const isMarkerRange = (rangeId) => typeof rangeId === 'string' && rangeId.startsWith(MARKER_PREFIX);
export const markerRangeId = (m) => MARKER_PREFIX + m.id;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const isValid = (m) =>
  m && typeof m.id === 'string' && m.id.length > 0 &&
  typeof m.name === 'string' && m.name.trim().length > 0 &&
  typeof m.date === 'string' && DATE_RE.test(m.date);
const byDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.name.localeCompare(b.name));

// A stable id at creation. randomUUID exists in every browser this admin runs
// in; the fallback is only so a missing API can never block a save.
const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

let state = { markers: [], loaded: false, error: null };
let inflight = null;
const listeners = new Set();
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const getSnapshot = () => state;
const setState = (patch) => { state = { ...state, ...patch }; listeners.forEach(fn => fn()); };

/** Synchronous read for code outside React (resolveTimeframe). */
export const getMarkers = () => state.markers;
export const findMarker = (rangeId) =>
  isMarkerRange(rangeId) ? (state.markers.find(m => markerRangeId(m) === rangeId) || null) : null;

function parseStored(raw) {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(isValid).sort(byDate) : [];
  } catch {
    return [];
  }
}

/** Fetch once; later calls return the cached list unless forced. */
export function loadMarkers(force = false) {
  if (inflight) return inflight;
  if (state.loaded && !force) return Promise.resolve(state.markers);
  inflight = getAdminSettings()
    .then(res => {
      const raw = res?.data?.[MARKERS_KEY]?.value;
      setState({ markers: parseStored(raw), loaded: true, error: null });
      return state.markers;
    })
    .catch(() => {
      // The built-in options must keep working; this only affects markers.
      setState({ loaded: true, error: 'Saved markers could not be loaded.' });
      return state.markers;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

// The whole list is written every time. Nothing is put in the local list until
// the server has accepted it, so a marker is never shown as saved when it is not.
async function persist(next) {
  await saveAdminSetting(MARKERS_KEY, JSON.stringify(next));
  setState({ markers: next, error: null });
}

/** @returns the created marker; rejects (list unchanged) if the save fails. */
export async function addMarker({ name, date }) {
  const marker = { id: newId(), name: String(name).trim(), date: String(date).slice(0, 10) };
  if (!isValid(marker)) throw new Error('A marker needs a name and a date.');
  await persist([...state.markers, marker].sort(byDate));
  return marker;
}

/** Rejects (list unchanged) if the save fails. */
export async function deleteMarker(id) {
  await persist(state.markers.filter(m => m.id !== id));
}

/** Subscribe a component to the shared list, loading it on first use. */
export function useMarkers() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => { loadMarkers(); }, []);
  return snap;   // { markers, loaded, error }
}
